import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Wheat } from "lucide-react";

export default function AuthPage({ mode = "login" }) {
  const isLogin = mode === "login";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [persona, setPersona] = useState("trader");
  const [busy, setBusy] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (isLogin) await login(email, password);
      else await register({ email, password, name, persona });
      toast.success(isLogin ? "Welcome back" : "Account created");
      navigate("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Authentication failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="relative grid min-h-screen bg-background lg:grid-cols-[1fr_1.1fr]">
      <div className="hidden flex-col justify-between border-r border-border/60 bg-card/40 p-10 lg:flex">
        <Link to="/" className="flex items-center gap-2">
          <div className="grid h-8 w-8 place-items-center rounded-md bg-primary/15 text-primary"><Wheat className="h-4 w-4" /></div>
          <div>
            <div className="font-display text-sm font-bold">AgriTerminal <span className="text-primary">Pro</span></div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Buying Consultant</div>
          </div>
        </Link>
        <div>
          <h1 className="font-display text-4xl font-bold leading-tight tracking-tight">
            Terminal-grade timing<br />for agri procurement.
          </h1>
          <p className="mt-4 max-w-md text-sm leading-relaxed text-muted-foreground">
            Live prices, AI Buy/Hold/Wait signals, vendor benchmarking and analyst notes across 8 launch commodities.
          </p>
          <div className="mt-6 grid grid-cols-4 gap-1 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
            <div className="rounded border border-border/60 bg-background/70 px-2 py-1 text-center">Wheat</div>
            <div className="rounded border border-border/60 bg-background/70 px-2 py-1 text-center">Corn</div>
            <div className="rounded border border-border/60 bg-background/70 px-2 py-1 text-center">Soy</div>
            <div className="rounded border border-border/60 bg-background/70 px-2 py-1 text-center">Cotton</div>
            <div className="rounded border border-border/60 bg-background/70 px-2 py-1 text-center">Coffee</div>
            <div className="rounded border border-border/60 bg-background/70 px-2 py-1 text-center">Sugar</div>
            <div className="rounded border border-border/60 bg-background/70 px-2 py-1 text-center">Rice</div>
            <div className="rounded border border-border/60 bg-background/70 px-2 py-1 text-center">Palm</div>
          </div>
        </div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
          Market insight, not financial advice
        </div>
      </div>
      <div className="flex items-center justify-center p-6 lg:p-12">
        <Card className="w-full max-w-md border-border/60 bg-card/70 backdrop-blur-md">
          <CardHeader>
            <CardTitle className="font-display text-2xl">
              {isLogin ? "Log in" : "Create your terminal"}
            </CardTitle>
            <CardDescription>
              {isLogin ? "Access your dashboard, watchlists and alerts." : "Free tier: delayed prices, weekly summary, notes."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-4">
              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="name">Full name</Label>
                  <Input id="name" data-testid="auth-name" value={name} onChange={e => setName(e.target.value)} required />
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" data-testid="auth-email" value={email} onChange={e => setEmail(e.target.value)} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input id="password" type="password" data-testid="auth-password" value={password} onChange={e => setPassword(e.target.value)} required minLength={6} />
              </div>
              {!isLogin && (
                <div className="space-y-2">
                  <Label>I am a…</Label>
                  <Select value={persona} onValueChange={setPersona}>
                    <SelectTrigger data-testid="auth-persona"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="trader">Trader</SelectItem>
                      <SelectItem value="procurement">Procurement manager</SelectItem>
                      <SelectItem value="farmer">Farmer / Wholesaler</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <Button type="submit" disabled={busy} className="w-full rounded-full" data-testid="auth-submit">
                {busy ? "Please wait…" : isLogin ? "Log in" : "Create account"}
              </Button>
            </form>
            <div className="mt-4 text-center text-sm text-muted-foreground">
              {isLogin ? (
                <>New here? <Link data-testid="auth-switch" to="/register" className="text-primary hover:underline">Create an account</Link></>
              ) : (
                <>Already have an account? <Link data-testid="auth-switch" to="/login" className="text-primary hover:underline">Log in</Link></>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
